import React, { useState } from 'react';
import { createPortal } from 'react-dom';

// "Save to…" sheet shown when the user saves a product from chat.
// Pick Wishlist / My shelf (+ usage tags for the shelf). Sofia 2026-06-30.
const USAGE_TAGS = [
  { key: 'in_rotation', label: 'In rotation', bg: '#EAF1EA', fg: '#5C7A57' },
  { key: 'now_and_then', label: 'Now & then', bg: '#FBF1E4', fg: '#8A5A2A' },
  { key: 'seasonal', label: 'Seasonal', bg: '#EAEFF5', fg: '#51708F' },
  { key: 'for_concerns', label: 'For concerns', bg: '#F5EAEA', fg: '#9A5A5A' },
];

// Module-level so React keeps a stable component identity across renders
// (defining it inline made React reuse DOM nodes and misfire onClick).
function ListBtn({ active, onClick, label, sub }) {
  return (
    <button type="button" onClick={onClick}
      className="flex-1 rounded-xl px-3 py-2 text-left transition-colors"
      style={active
        ? { background: '#2A1212', color: '#fff' }
        : { background: '#fff', color: '#2A1212', border: '1px solid #E5E0D8' }}>
      <span className="block text-[13.5px] font-semibold leading-tight">{label}</span>
      <span className="block text-[13px] leading-tight" style={{ opacity: 0.7 }}>{sub}</span>
    </button>
  );
}

export default function SavePicker({ product, onClose, onSave }) {
  const [list, setList] = useState('shelf');       // 'shelf' | 'wishlist'
  const [tags, setTags] = useState(['in_rotation']);
  const [selectedShadeName, setSelectedShadeName] = useState(product?.recommended_shade || '');
  if (!product) return null;

  const shadeOptions = Array.isArray(product.shade_options)
    ? product.shade_options.filter((shade) => shade?.name)
    : [];
  const toggleTag = (k) => setTags((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));
  const handleSave = () => {
    const selectedShade = shadeOptions.find((shade) => shade.name === selectedShadeName);
    onSave({
      list_category: list === 'wishlist' ? 'wishlist' : 'current',
      usage_tags: list === 'shelf' ? tags : [],
      ...(selectedShade ? {
        recommended_shade: selectedShade.name,
        swatch_hex: selectedShade.hex || null,
        swatch_image_url: selectedShade.imageUrl || null,
      } : {}),
    });
  };

  // Portal to <body> so the sheet escapes the animated message-list stacking context —
  // otherwise the chat input bar (z-index 10 in its OWN context) renders over the picker
  // and buries the Save button + tags (Sofia's #49 "big problem"). z-[300] sits above
  // every app layer; safe-area padding keeps Save clear of the iOS home indicator.
  return createPortal((
    <div className="fixed inset-0 z-[300]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain bg-white rounded-t-2xl" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }} onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-[#E5E0D8] mx-auto my-3" />
        <div className="px-5">
          <p key="name" className="text-[15px] font-semibold text-[#2A1212] leading-snug break-words">
            {product.name}{product.brand ? ` · ${product.brand}` : ''}
          </p>
          <p key="title" className="text-[13px] text-[#2A324B]/70 mt-0.5">Choose where to save this product</p>

          {shadeOptions.length === 1 && (
            <p className="mt-3 text-[13px] text-[#2A324B]">Saving shade: <span className="font-semibold break-words">{shadeOptions[0].name}</span></p>
          )}
          {shadeOptions.length > 1 && (
            <fieldset className="mt-4">
              <legend className="text-[13px] font-semibold text-[#2A324B] mb-2">Choose the shade to save</legend>
              <div className="space-y-2">
                {shadeOptions.map((shade) => {
                  const selected = shade.name === selectedShadeName;
                  return (
                    <label key={shade.name} className={`flex items-center gap-2.5 rounded-xl border-2 px-2.5 py-2 cursor-pointer focus-within:ring-2 focus-within:ring-[#2A324B]/25 ${selected ? 'border-[#2A324B] bg-[#F7F7F9]' : 'border-[#2A324B]/15 bg-white'}`}>
                      <input type="radio" name="save-shade" value={shade.name} checked={selected} onChange={() => setSelectedShadeName(shade.name)} className="sr-only" />
                      <span className="w-9 h-9 flex-shrink-0 rounded-lg border border-[#2A324B]/10" style={{ backgroundColor: shade.hex || '#F4F1EC', backgroundImage: !shade.hex && shade.imageUrl ? `url(${shade.imageUrl})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }} aria-hidden="true" />
                      <span className="min-w-0 flex-1 text-[13px] font-medium text-[#2A1212] leading-snug break-words">{shade.name}</span>
                      <span className={`w-4 h-4 flex-shrink-0 rounded-full border-2 ${selected ? 'border-[#2A324B] bg-[#2A324B]' : 'border-[#2A324B]/30'}`} aria-hidden="true" />
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div key="toggle" className="flex gap-2.5 mt-4">
            <ListBtn active={list === 'shelf'} onClick={() => setList('shelf')} label="My shelf" sub="I own it" />
            <ListBtn active={list === 'wishlist'} onClick={() => setList('wishlist')} label="Wishlist" sub="I want it" />
          </div>

          {list === 'shelf' && (
            <div key="tags" className="mt-4">
              <p className="text-[13px] text-[#2A324B]/50 mb-2">How do you use it?</p>
              <div className="flex flex-wrap gap-2">
                {USAGE_TAGS.map((t) => {
                  const on = tags.includes(t.key);
                  return (
                    <button key={t.key} type="button" onClick={() => toggleTag(t.key)}
                      className="text-[13px] font-medium rounded-full px-3 py-1.5 transition-colors"
                      style={on
                        ? { background: t.bg, color: t.fg, boxShadow: `inset 0 0 0 1.5px ${t.fg}` }
                        : { background: '#fff', color: '#2A324B', border: '1px solid #E5E0D8' }}>
                      {on ? '✓ ' : ''}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button key="save" type="button" onClick={handleSave}
            className="w-full mt-5 rounded-xl py-3 text-[15px] font-semibold text-white"
            style={{ background: '#2A1212' }}>
            {list === 'shelf' ? 'Save to My shelf' : 'Save to Wishlist'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
